import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { BasisTheoryProvider, useBasisTheory } from '@basis-theory/react-elements';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { fromJS } from 'immutable';
import flowRight from 'lodash-es/flowRight';
import { paymentMethod as PaymentMethods, acceptableCancelCodesForRewrite } from '@ourbranch/lookups';
import { isFuture, parseISO } from 'date-fns';

import { hydrateFarmPartyForForm } from 'common/helpers/farm-interested-party-helpers';
import config from 'aws-exports';
import { useBasisTheoryElementRefsProvider } from 'common/hooks/basis-theory-elements.context';
import { withStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { withToast } from 'core/components/toast/context';
import { track } from 'core/helpers/analytics';
import { getPolicyStatus, PolicyStatus, isImportedAndNotEndorseable } from 'core/helpers/policy-status';
import {
  showExpirationInsteadOfEndDate,
  endDateToExpirationDisplay,
  expirationDisplayToEndDate
} from 'core/helpers/expiration-date';
import { ValidationErrorsModal, useValidationErrorsModal } from 'core/components/validation-errors-modal';
import { getStartMinMaxDate, localToUtcTime } from '../dates';
import Footer from '../footer';
import CancelledPolicyFooter from '../footer/cancelled-policy-footer';
import ImportedPolicyFooter from '../footer/imported-policy-footer';
import CancellationForm from '../footer/cancellation-form';
import NonRenewForm from '../footer/non-renew-form';
import ReinstateForm from '../footer/reinstate-form';
import { AddHoldCard } from '../hold-card';
import RewriteModal from '../rewrite-modal';
import { validationSchema } from './policy-settings.validation';
import PolicySettings from './policy-settings';
import NoReinstatementModal from './no-reinstatement-modal';
import MismatchBillingDetails from './mismatch-billing-details-modal';
import useStyles from './policy-settings.styles';

const stripePromise = loadStripe(config.stripeKey);

const endDateValue = (values) => {
  if (values.reinstateDate) {
    return values.reinstateDate;
  }
  // When the brand displays the expiration date, the form holds expiration for an in-force policy;
  // convert back to the stored endDate (expiration − 1). A flat cancellation (endDate ===
  // effectiveDate, never in force) is not shifted, so it's saved as-is. (#32070)
  if (showExpirationInsteadOfEndDate() && values.endDate && values.endDate !== values.effectiveDate) {
    return expirationDisplayToEndDate(values.endDate);
  }
  return values.endDate;
};

const parseValues = (values, isReinstatementOrRescind) => ({
  ...values,
  cancelReason: values.cancelReason,
  nonRenewReason: values.autoRenew ? 'NA' : values.nonRenewReason,
  nonRenewed: !values.autoRenew,
  endDate: endDateValue(values),
  // for reinstatement, don't send in fees because the backend will determine what fees to generate on a reinstatement
  fees: isReinstatementOrRescind ? undefined : values.fees,
  fullTermCancel: isReinstatementOrRescind ? false : values.fullTermCancel
});

const getPrimaryMortgage = ({ policy, primaryMortgageForBilling }) => {
  const emptyMortgage = {
    loanNumber: undefined,
    mortgageHolderAddress: undefined,
    mortgageHolderName: undefined,
    mortgageHolderId: undefined,
    mortgageHolderEmail: undefined,
    mortgageHolderFax: undefined
  };

  if (policy?.defaultEscrowAccount?.mortgageHolderName) {
    return policy.defaultEscrowAccount;
  }
  return primaryMortgageForBilling || emptyMortgage;
};

const doesPolicyHaveCancelledPriorTerm = (policy, policies) => {
  if (policy.term === 1 || policies.length === 1) {
    // first term or only policy should not have cancelled prior
    return false;
  }

  // use 15 digits in case of home2 / auto3 etc.
  const priorTerm = policies.find(
    (p) => p.id.substring(0, 15) === policy.id.substring(0, 15) && Number(p.id.split('-')[2]) === policy.term - 1
  );

  if (priorTerm && isFuture(parseISO(policy.effectiveDate)) && priorTerm.endDate !== priorTerm.fullTermPolicyEndDate) {
    // check if the new policy would have started already, if so we don't need to reopen the prior term
    return priorTerm.id;
  }

  return false;
};

const isPolicyEligibleForRewrite = (policy, canRewritePolicy) => {
  if (!canRewritePolicy) {
    return false;
  }
  const flatCancel = policy?.effectiveDate === policy?.endDate;
  const firstTerm = policy?.term === 1;
  return firstTerm && flatCancel && acceptableCancelCodesForRewrite.includes(policy?.cancelReason);
};

const PolicySettingsContainer = ({
  loadingPreview,
  onPreviewPolicy,
  accountId,
  cancelledOrInCancellation,
  allowRescind,
  allowReinstatement,
  disableLeavePageModal,
  store: {
    account: {
      policies: {
        policy: { segment, setChanged, policy, billingDetails, addHoldCard, policyDetails },
        list: policies
      }
    }
  },
  toast
}) => {
  const [state, setState] = useState(
    fromJS({
      cancelPolicy: false,
      isNonRenew: false,
      effectiveDateTouched: false,
      modal: ''
    })
  );

  const classes = useStyles();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();
  const { canBackDate, isAgency, canEdit, viewOnly, canRewritePolicy, canReinstateCNPY } = useSession();

  const policyEligibleForRewrite = isPolicyEligibleForRewrite(policy, canRewritePolicy);
  const policyStatus = getPolicyStatus(policy);
  const { bt } = useBasisTheory(config.basisTheoryApiKey, {
    elements: true,
    environment: config.basisTheoryElementsEnv
  });
  const { Provider: BasisTheoryElementsProvider, refs: elementRefs } = useBasisTheoryElementRefsProvider();

  const [showNoReinstateForCNPYModal, setShowNoReinstateForCNPYModal] = useState(false);
  const [showRewriteModal, setShowRewriteModal] = useState(false);
  const [showNoReinstateForPriorCancelledModal, setShowNoReinstateForPriorCancelledModal] = useState(false);
  const [showMismatchModal, setShowMismatchModal] = useState(false);
  const property = policyDetails.home || policyDetails.condo || policyDetails.farm;
  const primaryMortgageForBilling = property?.mortgageDetails?.find((mortgage) => mortgage?.primary);
  const primaryMortgage = getPrimaryMortgage({ policy, primaryMortgageForBilling });
  const { defaultBankAccount, defaultCreditCard } = useMemo(() => {
    // not all accounts will have a default so set it from activePaymentMethod
    let defaultBankAccount = policy.defaultBankAccount;
    let defaultCreditCard = policy.defaultCreditCard;
    const activeAccount = billingDetails?.activePaymentMethod;

    const isBankAccount = (acct) => acct?.type === 'us_bank_account' || acct?.id?.startsWith('ba') || !!acct?.bankName;
    const isCreditCard = (acct) =>
      acct?.type === 'card' || acct?.id?.startsWith('card') || (!!acct?.brand && !acct?.bankName);

    const activeBankAccount = isBankAccount(activeAccount)
      ? activeAccount
      : billingDetails?.allPaymentMethods?.find(isBankAccount);
    const activeCreditCard = isCreditCard(activeAccount)
      ? activeAccount
      : billingDetails?.allPaymentMethods?.find(isCreditCard);

    // Stripe and the policy can drift (e.g. someone updated the customer directly in Stripe).
    // Only override when Stripe has a concrete value that disagrees with the policy — never clobber
    // a valid policy.defaultX with `{ id: undefined }` just because Stripe returned nothing
    // (which also happens during stale-cache windows right after a confirm). See #29196.
    if (activeBankAccount?.id && activeBankAccount.id !== policy.defaultBankAccount?.id) {
      defaultBankAccount = activeBankAccount;
    }
    if (activeCreditCard?.id && activeCreditCard.id !== policy.defaultCreditCard?.id) {
      defaultCreditCard = activeCreditCard;
    }
    return { defaultBankAccount, defaultCreditCard };
  }, [policy, billingDetails]);

  const { minDate, maxDate } = getStartMinMaxDate({
    policy,
    editCurrentSegment: true,
    currentSegment: policy.segments[0],
    canBackDate,
    editingPolicySettings: true,
    policyType: policy.policyType,
    isAgency
  });

  const priorCancelledTerm = doesPolicyHaveCancelledPriorTerm(policy, policies);
  const cancelledFooterActionText =
    (allowReinstatement && 'Reinstate Policy') || (allowRescind && 'Rescind Cancellation');
  const isReinstatementOrRescind = allowReinstatement || allowRescind;

  const handleCancellation = useCallback(
    (cancelPolicy, changed = true) => {
      setState(state.setIn(['cancelPolicy'], cancelPolicy).setIn(['changed'], changed));
    },
    [setState, state]
  );

  const handleAddHoldCard = useCallback(() => {
    setState(state.setIn(['modal'], 'addHoldCard'));
  }, [setState, state]);

  const closeModal = useCallback(() => {
    setState(state.setIn(['modal'], ''));
  }, [setState, state]);

  const handleChangePolicy = useCallback(
    (values) => {
      const parsed = parseValues(values, isReinstatementOrRescind);
      // The form seeds defaultEscrowAccount once at mount, so it can be empty even when the policy
      // has a primary mortgage (validation accepts that case); backfill it here so the saved policy
      // always carries the escrow account.
      if (
        parsed.paymentMethod === PaymentMethods.Escrow &&
        !parsed.defaultEscrowAccount?.mortgageHolderName &&
        primaryMortgage?.mortgageHolderName
      ) {
        parsed.defaultEscrowAccount = primaryMortgage;
      }
      onPreviewPolicy(parsed);
    },
    [onPreviewPolicy, isReinstatementOrRescind, primaryMortgage]
  );

  const onHoldCardAddition = useCallback(
    (args) => {
      const { reason, policyId } = args;
      addHoldCard(reason, policyId)
        .then((res) => {
          track('Staff Add Hold Card', { args, res });
          toast.notify({
            type: 'success',
            message: 'The hold card was added successfully'
          });
        })
        .catch((res) => {
          Sentry.captureException(
            JSON.stringify({
              res,
              body: {
                reason,
                policyId
              }
            })
          );
        });
    },
    [addHoldCard, toast]
  );

  return (
    <Formik
      initialValues={{
        ...policy,
        // When the brand displays the expiration date instead of endDate, seed the form with the
        // expiration (endDate + 1) for an in-force policy so the field shows/edits expiration; the
        // stored value is restored on save via endDateValue. Flat cancellations are left as-is. (#32070)
        endDate:
          showExpirationInsteadOfEndDate() && policy.endDate && policy.endDate !== policy.effectiveDate
            ? endDateToExpirationDisplay(policy.endDate)
            : policy.endDate,
        // farmInterestedParties is a policy-level field (like the other settings-tab fields), not a
        // segment field — so it must be read from `policy`, not the segment-merged `policyDetails`.
        farmInterestedParties: (policy.farmInterestedParties || []).map(hydrateFarmPartyForForm),
        defaultEscrowAccount: primaryMortgage,
        renewalCreditPull: !!policy.renewalCreditPull,
        blockCreditCheckAtRenewal: policy?.blockCreditCheckAtRenewal || false,
        defaultBankAccount,
        defaultCreditCard,
        cancellationEndDate: policy.endDate,
        isNonRenew: false,
        cancelReason: policy.cancelReason === 'NA' ? undefined : policy.cancelReason, // clear out the NA value because policies are bound with this
        nonRenewReason: policy.nonRenewReason === 'NA' ? undefined : policy.nonRenewReason, // clear out the NA value because policies are bound with this
        skipImmediateBillOrRefund: policy.skipImmediateBillOrRefund,
        salesRep: policy.salesRep || policy?.offer?.quote?.rep,
        renewalPaymentMethod: policy.renewalPaymentMethod ?? policy.paymentMethod,
        applyFee: policyStatus === PolicyStatus.Cancelled
      }}
      validationSchema={() => {
        if (cancelledOrInCancellation) {
          // dont validate anything on reinstatements/recinding cancellations - cant return undefined for some reason
          return Yup.object();
        }
        try {
          return Yup.lazy((values) =>
            validationSchema({
              policyType: policy.policyType,
              values,
              minDate,
              maxDate,
              currentEffectiveDate: policy.effectiveDate,
              wasEffectiveDateUpdated:
                policy.effectiveDate !== values.effectiveDate && state.get('effectiveDateTouched'),
              policyStatus,
              policyDetails,
              billingDetails,
              term: policy.term,
              agentCanBackDate: canBackDate,
              primaryMortgage
            })
          );
        } catch (e) {
          if (e?.name?.includes('TypeError')) {
            console.error(e);
            Sentry.captureException(e, {
              extra: {
                inPolicySettings: true,
                validationFailedToRun: true,
                policyId: policy.id,
                policyType: policy.policyType,
                policyStatus
              }
            });
          }
          return Yup.object();
        }
      }}
      onSubmit={handleChangePolicy}
    >
      {({ handleSubmit, touched, values, validateForm, setErrors, setTouched }) => {
        const {
          cancel,
          endDate,
          fullTermPolicyEndDate,
          state: policyState,
          paymentMethod,
          paymentType,
          renewalPaymentMethod,
          renewalPaymentType
        } = values;
        const changed = canEdit && Object.keys(touched).length > 0;
        // `endDate` may be shifted to the expiration date for display (#32070); compare against the
        // stored endDate (last full day) so cancellation detection stays correct. fullTermPolicyEndDate
        // is always stored canonically.
        const storedEndDate =
          showExpirationInsteadOfEndDate() && endDate && endDate !== values.effectiveDate
            ? expirationDisplayToEndDate(endDate)
            : endDate;
        const policyEndDateIsBeforeOriginalEndDate =
          localToUtcTime(`${storedEndDate}T23:59:59.99`, policyState) <
          localToUtcTime(`${fullTermPolicyEndDate}T23:59:59.99`, policyState);

        const previewPolicyCancellation = cancelledOrInCancellation || cancel || policyEndDateIsBeforeOriginalEndDate;

        // will submit form and show validation errors if there are any
        const handleKeepCurrentInfo = async () => {
          const errors = await validateForm();
          if (Object.keys(errors).length) {
            setErrors(errors);
            setTouched(errors);
            setShowValidationErrorsModal(true);
          } else {
            handleSubmit();
          }
        };

        // handles modals to show before submitting the form and checking for validation errors
        const onSubmit = async () => {
          setState(state.setIn(['effectiveDateTouched'], !!touched.effectiveDate));

          // if the policy is CANCELLED and it was cancelled due to non-payment then we need to show the no reinstatement modal
          // exceptions: if user has permission group or the policy is Escrow payment method
          if (
            policyStatus === PolicyStatus.Cancelled &&
            policy?.cancelReason &&
            policy.cancelReason === 'CNPY' &&
            !canReinstateCNPY &&
            paymentMethod !== PaymentMethods.Escrow
          ) {
            setShowNoReinstateForCNPYModal(true);
            return;
          }
          if (cancelledOrInCancellation && isReinstatementOrRescind && priorCancelledTerm) {
            setShowNoReinstateForPriorCancelledModal(true);
            return;
          }
          if (
            (paymentMethod !== renewalPaymentMethod || paymentType !== renewalPaymentType) &&
            (Object.keys(touched).includes('paymentMethod') || Object.keys(touched).includes('paymentType'))
          ) {
            setShowMismatchModal(true);
            return;
          }
          handleKeepCurrentInfo();
        };

        setChanged(changed);

        return (
          <BasisTheoryProvider bt={bt}>
            <BasisTheoryElementsProvider value={elementRefs}>
              {(showNoReinstateForCNPYModal || showNoReinstateForPriorCancelledModal) && (
                <NoReinstatementModal
                  open={showNoReinstateForCNPYModal || showNoReinstateForPriorCancelledModal}
                  setShowNoReinstateForCNPYModal={setShowNoReinstateForCNPYModal}
                  showNoReinstateForCNPYModal={showNoReinstateForCNPYModal}
                  setShowNoReinstateForPriorCancelledModal={setShowNoReinstateForPriorCancelledModal}
                  showNoReinstateForPriorCancelledModal={showNoReinstateForPriorCancelledModal}
                  priorCancelledTerm={priorCancelledTerm}
                />
              )}

              <MismatchBillingDetails
                open={showMismatchModal}
                onSubmit={handleKeepCurrentInfo}
                onClose={() => setShowMismatchModal(false)}
              />
              <Elements stripe={stripePromise}>
                <div className={changed || cancelledOrInCancellation ? classes.changed : null}>
                  <PolicySettings
                    key={`settings-status-${state.get('cancelPolicy')}`}
                    policy={values}
                    cancelPolicy={state.get('cancelPolicy')}
                    handleCancellation={handleCancellation}
                    changed={changed}
                    segment={segment}
                    handleAddHoldCard={handleAddHoldCard}
                    accountId={accountId}
                    minDate={minDate}
                    maxDate={maxDate}
                    cancelledOrInCancellation={cancelledOrInCancellation}
                  />

                  {!viewOnly &&
                    (isImportedAndNotEndorseable(policy) ? (
                      <ImportedPolicyFooter />
                    ) : cancelledOrInCancellation ? (
                      isReinstatementOrRescind || policyEligibleForRewrite ? (
                        <Footer
                          title={cancelledFooterActionText}
                          buttonLabel={cancelledFooterActionText}
                          form={priorCancelledTerm ? null : <ReinstateForm />}
                          loading={loadingPreview}
                          onClick={onSubmit}
                          setShowRewriteModal={setShowRewriteModal}
                          isPolicyEligibleForRewrite={policyEligibleForRewrite}
                          policy={policy}
                          disableLeavePageModal={disableLeavePageModal}
                        />
                      ) : (
                        <CancelledPolicyFooter
                          policyStatus={policyStatus}
                          isAgency={isAgency}
                          endDate={policy.endDate}
                          effectiveDate={policy.effectiveDate}
                          policy={policy}
                        />
                      )
                    ) : (
                      changed &&
                      (values.isNonRenew ? (
                        <Footer
                          title="Cancel Renewal"
                          form={<NonRenewForm />}
                          loading={loadingPreview}
                          onClick={onSubmit}
                          policy={policy}
                          disableLeavePageModal={disableLeavePageModal}
                        />
                      ) : (
                        <Footer
                          title={cancel ? 'Cancel Policy' : 'Save Changes'}
                          buttonLabel={previewPolicyCancellation ? 'Preview Cancellation' : undefined}
                          form={previewPolicyCancellation && <CancellationForm />}
                          loading={loadingPreview}
                          onClick={onSubmit}
                          policy={policy}
                          disableLeavePageModal={disableLeavePageModal}
                        />
                      ))
                    ))}
                  {state.getIn(['modal']) === 'addHoldCard' && (
                    <AddHoldCard
                      policy={policy}
                      onClose={closeModal}
                      onHoldCardAddition={onHoldCardAddition}
                      open={state.getIn(['modal']) === 'addHoldCard'}
                    />
                  )}
                  <ValidationErrorsModal
                    open={showValidationErrorsModal}
                    onClose={() => setShowValidationErrorsModal(false)}
                  />
                  {showRewriteModal && <RewriteModal setShowRewriteModal={setShowRewriteModal} />}
                </div>
              </Elements>
            </BasisTheoryElementsProvider>
          </BasisTheoryProvider>
        );
      }}
    </Formik>
  );
};
PolicySettingsContainer.propTypes = {
  policy: PropTypes.shape({
    renewalCreditPull: PropTypes.bool,
    policyType: PropTypes.string.isRequired,
    effectiveDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    billingDayOfMonth: PropTypes.number.isRequired,
    autoRenew: PropTypes.bool,
    state: PropTypes.string.isRequired,
    term: PropTypes.number.isRequired,
    version: PropTypes.number.isRequired
  }).isRequired,
  loadingPreview: PropTypes.bool.isRequired,
  onPreviewPolicy: PropTypes.func.isRequired,
  toast: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  accountId: PropTypes.string.isRequired,
  cancelledOrInCancellation: PropTypes.bool.isRequired,
  allowRescind: PropTypes.bool.isRequired,
  allowReinstatement: PropTypes.bool.isRequired
};

export default flowRight(withToast, withStore)(PolicySettingsContainer);
